/**
 * EM Erklärvideos — modal, filter, lesson tracks (eigene Slider), „Gesehen“
 */
(() => {
  if (customElements.get("em-erklaervideos")) return;

  const WATCHED_KEY = "em-erklaervideos-watched";
  const NAV_PREV_SVG =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 5L8 12l7 7" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  const NAV_NEXT_SVG =
    '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  class EmErklaervideos extends HTMLElement {
    #lesson = "";
    #season = "";
    #year = "";
    #watched = new Set();
    #sliders = [];

    connectedCallback() {
      this.modal = this.querySelector("[data-em-ev-modal]");
      this.player = this.querySelector("[data-em-ev-player]");
      this.tracks = this.querySelector("[data-em-ev-tracks]");
      this.moreLabel = this.getAttribute("data-more-label") || "Mehr anzeigen";
      this.lessLabel = this.getAttribute("data-less-label") || "Weniger anzeigen";

      this.#sortCardsInSource();
      this.#buildLessonTracks();
      this.#initFilters();
      this.#initWatched();
      this.#bindSliders();
      this.#applyFilters();

      if (this.modal && this.player) {
        if (this.modal.parentElement !== document.body) {
          document.body.appendChild(this.modal);
        }
      }

      this.addEventListener("click", this.#onClick);
      this.addEventListener("change", this.#onChange);
      document.addEventListener("keydown", this.#onKeydown);
      window.addEventListener("resize", this.#onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.#onClick);
      this.removeEventListener("change", this.#onChange);
      document.removeEventListener("keydown", this.#onKeydown);
      window.removeEventListener("resize", this.#onScroll);
      this.#sliders.forEach(({ grid }) => {
        grid?.removeEventListener("scroll", this.#onScroll);
      });
    }

    #cards() {
      return Array.from(this.querySelectorAll("[data-em-ev-card]"));
    }

    #sourceGrid() {
      return this.querySelector("[data-em-ev-track] [data-em-ev-grid]");
    }

    #sortCardsInSource() {
      const grid = this.#sourceGrid();
      if (!grid) return;
      const cards = Array.from(grid.querySelectorAll("[data-em-ev-card]"));
      cards.sort((a, b) => this.#compareCards(a, b));
      cards.forEach((card) => grid.appendChild(card));
    }

    #compareCards(a, b) {
      const la = a.getAttribute("data-lesson-label") || "";
      const lb = b.getAttribute("data-lesson-label") || "";
      if (la && lb && la !== lb) return la.localeCompare(lb, "de", { numeric: true });
      const da = a.getAttribute("data-date") || "";
      const db = b.getAttribute("data-date") || "";
      if (da === db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.localeCompare(da);
    }

    #uniqueOptions(attr, labelAttr) {
      const map = new Map();
      this.#cards().forEach((card) => {
        const key = card.getAttribute(attr);
        const label = card.getAttribute(labelAttr) || key;
        if (key) map.set(key, label);
      });
      return [...map.entries()].sort((a, b) =>
        a[1].localeCompare(b[1], "de", { numeric: true })
      );
    }

    #createTrack(lessonKey, lessonLabel) {
      const track = document.createElement("div");
      track.className = "em-erklaervideos__track";
      track.setAttribute("data-em-ev-track", "");
      track.setAttribute("data-lesson", lessonKey || "");

      const title = document.createElement("h3");
      title.className = "em-erklaervideos__track-title";
      title.setAttribute("data-em-ev-track-title", "");
      if (lessonLabel) {
        title.textContent = lessonLabel;
        title.hidden = false;
      } else {
        title.hidden = true;
      }
      track.appendChild(title);

      const slider = document.createElement("div");
      slider.className = "em-erklaervideos__slider";
      slider.setAttribute("data-em-ev-slider", "");

      const prev = document.createElement("button");
      prev.type = "button";
      prev.className = "em-erklaervideos__nav em-erklaervideos__nav--prev";
      prev.setAttribute("data-em-ev-prev", "");
      prev.setAttribute("aria-label", "Vorherige Videos");
      prev.innerHTML = NAV_PREV_SVG;

      const next = document.createElement("button");
      next.type = "button";
      next.className = "em-erklaervideos__nav em-erklaervideos__nav--next";
      next.setAttribute("data-em-ev-next", "");
      next.setAttribute("aria-label", "Nächste Videos");
      next.innerHTML = NAV_NEXT_SVG;

      const grid = document.createElement("div");
      grid.className = "em-erklaervideos__grid";
      grid.setAttribute("data-em-ev-grid", "");

      slider.append(prev, next, grid);
      track.appendChild(slider);
      return { track, grid, prev, next };
    }

    #buildLessonTracks() {
      if (!this.tracks) return;

      const sourceTrack = this.tracks.querySelector("[data-em-ev-track]");
      const sourceGrid = sourceTrack?.querySelector("[data-em-ev-grid]");
      if (!sourceTrack || !sourceGrid) return;

      const lessons = this.#uniqueOptions("data-lesson", "data-lesson-label");
      const cards = Array.from(sourceGrid.querySelectorAll("[data-em-ev-card]"));

      // Nur ein Slider, wenn weniger als 2 Lektionen
      if (lessons.length < 2) {
        sourceTrack.setAttribute("data-lesson", lessons[0]?.[0] || "");
        return;
      }

      const frag = document.createDocumentFragment();
      const built = [];

      lessons.forEach(([key, label]) => {
        const parts = this.#createTrack(key, label);
        cards
          .filter((card) => card.getAttribute("data-lesson") === key)
          .sort((a, b) => this.#compareCards(a, b))
          .forEach((card) => parts.grid.appendChild(card));
        built.push(parts);
        frag.appendChild(parts.track);
      });

      // Videos ohne Lektion → eigener Block am Ende
      const unassigned = cards.filter((card) => !card.getAttribute("data-lesson"));
      if (unassigned.length) {
        const parts = this.#createTrack("", "Weitere Videos");
        unassigned.forEach((card) => parts.grid.appendChild(card));
        built.push(parts);
        frag.appendChild(parts.track);
      }

      this.tracks.replaceChildren(frag);
    }

    #bindSliders() {
      this.#sliders = [];
      this.querySelectorAll("[data-em-ev-slider]").forEach((slider) => {
        const grid = slider.querySelector("[data-em-ev-grid]");
        const prev = slider.querySelector("[data-em-ev-prev]");
        const next = slider.querySelector("[data-em-ev-next]");
        if (!grid) return;

        const entry = { slider, grid, prev, next };
        this.#sliders.push(entry);

        prev?.addEventListener("click", () => this.#scrollBy(entry, -1));
        next?.addEventListener("click", () => this.#scrollBy(entry, 1));
        grid.addEventListener("scroll", this.#onScroll, { passive: true });
      });
      this.#updateAllNav();
    }

    #initFilters() {
      const toolbar = this.querySelector("[data-em-ev-toolbar]");
      if (!toolbar) return;

      let anyVisible = false;

      const lessonGroup = this.querySelector("[data-em-ev-lesson-group]");
      const lessonSelect = this.querySelector("[data-em-ev-lesson]");
      const lessons = this.#uniqueOptions("data-lesson", "data-lesson-label");
      if (lessonGroup && lessonSelect && lessons.length >= 2) {
        lessons.forEach(([key, label]) => {
          const option = document.createElement("option");
          option.value = key;
          option.textContent = label;
          lessonSelect.appendChild(option);
        });
        lessonGroup.hidden = false;
        anyVisible = true;
        // „Alle“ = beide Slider sichtbar
        lessonSelect.value = "";
        this.#lesson = "";
        lessonSelect.addEventListener("change", () => {
          this.#lesson = lessonSelect.value;
          this.#applyFilters();
        });
      }

      const seasonGroup = this.querySelector("[data-em-ev-season-group]");
      const seasonSelect = this.querySelector("[data-em-ev-season]");
      const seasons = this.#uniqueOptions("data-season", "data-season-label");
      if (seasonGroup && seasonSelect && seasons.length >= 2) {
        seasons.forEach(([key, label]) => {
          const option = document.createElement("option");
          option.value = key;
          option.textContent =
            label.length > 42 ? `${label.slice(0, 40).trim()}…` : label;
          option.title = label;
          seasonSelect.appendChild(option);
        });
        seasonGroup.hidden = false;
        anyVisible = true;
        seasonSelect.addEventListener("change", () => {
          this.#season = seasonSelect.value;
          this.#applyFilters();
        });
      }

      const yearGroup = this.querySelector("[data-em-ev-year-group]");
      const yearSelect = this.querySelector("[data-em-ev-year]");
      const years = [
        ...new Set(this.#cards().map((c) => c.getAttribute("data-year")).filter(Boolean)),
      ].sort((a, b) => b.localeCompare(a));
      if (yearGroup && yearSelect && years.length >= 2) {
        years.forEach((year) => {
          const option = document.createElement("option");
          option.value = year;
          option.textContent = year;
          yearSelect.appendChild(option);
        });
        yearGroup.hidden = false;
        anyVisible = true;
        yearSelect.addEventListener("change", () => {
          this.#year = yearSelect.value;
          this.#applyFilters();
        });
      }

      toolbar.hidden = !anyVisible;
    }

    #applyFilters() {
      this.#cards().forEach((card) => {
        const matchSeason = !this.#season || card.getAttribute("data-season") === this.#season;
        const matchYear = !this.#year || card.getAttribute("data-year") === this.#year;
        // Lektion steuert ganze Tracks — Karten innerhalb des Tracks bleiben sichtbar (außer Season/Jahr)
        card.hidden = !(matchSeason && matchYear);
      });

      this.querySelectorAll("[data-em-ev-track]").forEach((track) => {
        const key = track.getAttribute("data-lesson") || "";
        const matchLesson = !this.#lesson || key === this.#lesson;
        const hasVisibleCard = Array.from(track.querySelectorAll("[data-em-ev-card]")).some(
          (card) => !card.hidden
        );
        track.hidden = !(matchLesson && hasVisibleCard);
      });

      this.#sliders.forEach(({ grid }) => {
        if (grid) grid.scrollLeft = 0;
      });
      this.#updateAllNav();
    }

    #loadWatched() {
      try {
        const raw = localStorage.getItem(WATCHED_KEY);
        const list = raw ? JSON.parse(raw) : [];
        this.#watched = new Set(Array.isArray(list) ? list.map(String) : []);
      } catch (e) {
        this.#watched = new Set();
      }
    }

    #saveWatched() {
      try {
        localStorage.setItem(WATCHED_KEY, JSON.stringify([...this.#watched]));
      } catch (e) {}
    }

    #initWatched() {
      this.#loadWatched();
      this.#cards().forEach((card) => {
        const id = card.getAttribute("data-em-ev-id");
        const input = card.querySelector("[data-em-ev-watched]");
        if (!id || !input) return;
        const seen = this.#watched.has(id);
        input.checked = seen;
        card.classList.toggle("is-watched", seen);
      });
    }

    #setWatched(card, seen) {
      const id = card.getAttribute("data-em-ev-id");
      if (!id) return;
      if (seen) this.#watched.add(id);
      else this.#watched.delete(id);
      card.classList.toggle("is-watched", seen);
      this.#saveWatched();
    }

    #onScroll = () => {
      this.#updateAllNav();
    };

    #visibleCardsIn(grid) {
      return Array.from(grid.querySelectorAll("[data-em-ev-card]")).filter((card) => !card.hidden);
    }

    #cardStep(grid) {
      const card = this.#visibleCardsIn(grid)[0];
      if (!card) return grid.clientWidth || 280;
      const styles = getComputedStyle(grid);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    #scrollBy(entry, direction) {
      if (!entry?.grid) return;
      const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      entry.grid.scrollBy({
        left: direction * this.#cardStep(entry.grid),
        behavior: prefersReduced ? "auto" : "smooth"
      });
    }

    #updateNav(entry) {
      if (!entry?.prev || !entry.next) return;
      entry.prev.hidden = true;
      entry.next.hidden = true;
    }

    #updateAllNav() {
      this.#sliders.forEach((entry) => this.#updateNav(entry));
    }

    #onChange = (event) => {
      const input = event.target.closest("[data-em-ev-watched]");
      if (!input || !this.contains(input)) return;
      const card = input.closest("[data-em-ev-card]");
      if (!card) return;
      this.#setWatched(card, input.checked);
    };

    #onClick = (event) => {
      if (event.target.closest("[data-em-ev-watched]") || event.target.closest(".em-erklaervideos__watched")) {
        return;
      }

      const moreBtn = event.target.closest("[data-em-ev-more]");
      if (moreBtn && this.contains(moreBtn)) {
        event.preventDefault();
        this.#toggleMore(moreBtn);
        return;
      }

      if (event.target.closest(".em-erklaervideos__product")) return;

      const openBtn = event.target.closest("[data-em-ev-open]");
      if (openBtn && this.contains(openBtn)) {
        event.preventDefault();
        this.#open(openBtn);
      }
    };

    #onKeydown = (event) => {
      if (event.key === "Escape" && this.modal?.classList.contains("is-open")) {
        this.#close();
      }
    };

    #toggleMore(button) {
      const card = button.closest("[data-em-ev-card]");
      const body = card?.querySelector("[data-em-ev-body]");
      if (!body) return;

      const expanded = button.getAttribute("aria-expanded") === "true";
      const next = !expanded;
      button.setAttribute("aria-expanded", String(next));
      body.hidden = !next;
      button.textContent = next ? this.lessLabel : this.moreLabel;
    }

    #open(trigger) {
      if (!this.modal || !this.player) return;
      const src = trigger.getAttribute("data-video-src");
      if (!src) return;

      const source = this.player.querySelector("source");
      if (source) {
        source.src = src;
        this.player.load();
      } else {
        this.player.src = src;
      }

      this.modal.hidden = false;
      this.modal.classList.add("is-open");
      document.documentElement.classList.add("em-erklaervideos-open");
      try {
        this.player.currentTime = 0;
      } catch (e) {}
      const playPromise = this.player.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    }

    #close() {
      if (!this.modal || !this.player) return;
      this.modal.classList.remove("is-open");
      this.modal.hidden = true;
      document.documentElement.classList.remove("em-erklaervideos-open");
      this.player.pause();
    }
  }

  document.addEventListener("click", (event) => {
    const closeEl = event.target.closest("[data-em-ev-close]");
    if (!closeEl) return;
    const modal = closeEl.closest("[data-em-ev-modal]");
    if (!modal) return;
    modal.classList.remove("is-open");
    modal.hidden = true;
    document.documentElement.classList.remove("em-erklaervideos-open");
    const player = modal.querySelector("[data-em-ev-player]");
    if (player) player.pause();
  });

  customElements.define("em-erklaervideos", EmErklaervideos);
})();
